import {
  MAX_IMAGE_BYTES,
  type AcceptedMime,
  type PreflightIssue,
  type PreflightRejection,
  type PreparedOrigin,
  type RejectedFormat,
} from '@react-apps/content-editor';

/**
 * O texto que o admin-dashboard mostra para cada recusa do pré-flight de imagem.
 *
 * O pré-flight mora em `@react-apps/content-editor` e devolve o **motivo**
 * tipado (`PreflightRejection`), sem frase, porque serve também ao
 * primum-pipelines, que diz a recusa na própria tela. A frase é daqui, e é a
 * mesma em todo lugar que recusa (lote do zip, troca de arquivo, imagem
 * escolhida no formulário e no comentário): o admin nunca lê duas versões da
 * mesma recusa.
 */

/** Para mensagem de erro — a lista, sem repetir literal por aí. */
const ACCEPTED_IMAGE_LABEL = 'PNG, JPEG, WebP';

/** "12,4 MB" — o separador decimal é o de quem lê a mensagem. */
export function formatMegabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} MB`;
}

/** O teto em texto, sem casa decimal ("15 MB"). */
export const MAX_IMAGE_LABEL = `${MAX_IMAGE_BYTES / (1024 * 1024)} MB`;

/**
 * O que fazer com um arquivo grande demais. Uma frase só, usada nos lugares
 * que recusam por tamanho — a instrução é o que falta numa mensagem que só
 * informa o número.
 */
const SHRINK_HINT =
  'Reduza a imagem (exporte em qualidade menor, ou redimensione para a largura que ela realmente precisa ter) e envie o arquivo novo.';

const REJECTED_FORMAT_LABEL: Record<RejectedFormat, string> = {
  PDF: 'PDF',
  SVG: 'SVG',
  UNKNOWN: 'formato desconhecido',
};

const MIME_LABEL: Record<AcceptedMime, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/webp': 'WebP',
};

export function describeRejection(
  filename: string,
  rejection: PreflightRejection,
): string {
  switch (rejection.cause) {
    case 'UNREADABLE':
      return `não foi possível ler "${filename}" de dentro do zip — o arquivo pode estar corrompido.`;
    case 'TOO_LARGE':
      return `${filename} tem ${formatMegabytes(rejection.sizeBytes)} — o limite é ${MAX_IMAGE_LABEL} por imagem. ${SHRINK_HINT}`;
    case 'CONVERTIBLE_FORMAT':
      return `${filename} tem extensão ${rejection.extension ?? 'sem extensão'} mas o conteúdo é ${rejection.detected}. Aceitos: ${ACCEPTED_IMAGE_LABEL}.`;
    case 'UNSUPPORTED_FORMAT':
      return `${filename} não é ${ACCEPTED_IMAGE_LABEL} (detectado: ${REJECTED_FORMAT_LABEL[rejection.detected]}) — troque o arquivo.`;
    case 'NO_BYTES_TO_CONVERT':
      return `não há bytes guardados de "${filename}" para converter — troque o arquivo.`;
    case 'UNDECODABLE':
      return `este navegador não conseguiu decodificar ${rejection.detected} — troque "${filename}" por PNG, JPEG ou WebP.`;
    case 'STILL_TOO_LARGE':
      return `"${filename}" convertida para WebP ainda tem ${formatMegabytes(rejection.sizeBytes)} — o limite é ${MAX_IMAGE_LABEL} por imagem. ${SHRINK_HINT}`;
  }
}

/** A frase da pendência, para o card que a mostra. */
export function describeIssue(issue: PreflightIssue): string {
  return describeRejection(issue.filename, issue.rejection);
}

/** O que aconteceu com a imagem que saiu da pendência ("convertida de AVIF…"). */
export function describeOrigin(origin: PreparedOrigin): string {
  return origin.kind === 'CONVERTED'
    ? `convertida de ${origin.from} para WebP`
    : `arquivo trocado (${MIME_LABEL[origin.mime]})`;
}
